import { PAGE_TITLE_SERVICE_NAME } from '@/constant/pageTitle';
import { EDIT_PROFILE_PATH, ROOT_PATH, V1_PROFILE_PATH } from '@/constant/routers/path';
import routes from '@/routers/routes';
import {
  appAuthPresentation,
  applyInitialAuthPresentationFromDom,
  fetchMeIsAuthenticated,
  syncAppAuthClass,
} from '@/utils/authMe';
import { isJobSearchListRoutePath } from '@/utils/jobSearch/isJobSearchListRoutePath';
import { createRouter, createWebHistory, type RouteLocationNormalizedLoaded } from 'vue-router';

/**
 * 認証セッションとルートの扱い。ポリシーはすべて `routes.ts` の `meta` で表現する。
 *
 * - ゲスト専用 — `requiresGuest`
 * - 要ログイン — `requiresAuth`
 * - 別タブのセッション変化後にフルリロード — `reloadAfterCrossTabAuthChange`
 */
const isGuestOnlyRoute = (route: RouteLocationNormalizedLoaded): boolean =>
  Boolean(route.meta.requiresGuest);

const isLoginRequiredRoute = (route: RouteLocationNormalizedLoaded): boolean =>
  Boolean(route.meta.requiresAuth);

const isCandidateResumeRequiredRoute = (route: RouteLocationNormalizedLoaded): boolean =>
  Boolean(route.meta.requiresCandidateResume);

const needsReloadAfterCrossTabSessionChange = (route: RouteLocationNormalizedLoaded): boolean =>
  Boolean(route.meta.reloadAfterCrossTabAuthChange);

const updateDocumentTitle = (route: RouteLocationNormalizedLoaded): void => {
  if (route.meta.usesHeadTitle) return;

  const title = route.meta.documentTitle;
  if (typeof title === 'string' && title.trim()) {
    document.title = title;
    return;
  }
  document.title = PAGE_TITLE_SERVICE_NAME;
};

let candidateHasResumeCache: { accessToken: string | null; hasResume: boolean } | null = null;
let candidateHasResumeRequest: {
  accessToken: string | null;
  promise: Promise<boolean>;
} | null = null;

const fetchCandidateHasResume = async (): Promise<boolean> => {
  const token = localStorage.getItem('access_token');
  if (candidateHasResumeCache?.accessToken === token) {
    return candidateHasResumeCache.hasResume;
  }
  if (candidateHasResumeRequest?.accessToken === token) {
    return candidateHasResumeRequest.promise;
  }

  const accessTokenForRequest = token;
  const request = (async () => {
    try {
      const response = await fetch(V1_PROFILE_PATH, {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) return false;

      const body = (await response.json().catch(() => ({}))) as {
        data?: { candidate_resume?: unknown | null };
      };
      const hasResume = body.data?.candidate_resume != null;
      candidateHasResumeCache = hasResume ? { accessToken: token, hasResume } : null;
      return hasResume;
    } catch {
      return false;
    } finally {
      if (candidateHasResumeRequest?.accessToken === accessTokenForRequest) {
        candidateHasResumeRequest = null;
      }
    }
  })();
  candidateHasResumeRequest = { accessToken: token, promise: request };

  return request;
};

const clearCandidateResumeCache = (): void => {
  candidateHasResumeCache = null;
  candidateHasResumeRequest = null;
};

/**
 * Rails フルページ（`#app` 無し）。Vue ヘッダーだけ更新しても SSR の本文は古いままなので、
 * 別タブでセッションが変わったあとはフルリロードでサーバに再評価させる。
 */
const isDocumentWithoutSpaAppShell = (): boolean =>
  typeof document !== 'undefined' && document.getElementById('app') === null;

/**
 * 別タブでのログイン／ログアウト後にタブへ戻ったとき、`appAuthPresentation` と画面表示のズレを補正する。
 */
const reconcileUiAfterCrossTabAuthChange = (
  previousPresentation: boolean | null,
  nextAuthenticated: boolean
): void => {
  const becameAuthenticated = nextAuthenticated && previousPresentation !== true;
  const becameGuest = !nextAuthenticated && previousPresentation === true;

  if (!becameAuthenticated && !becameGuest) {
    return;
  }

  const route = router.currentRoute.value;

  if (becameAuthenticated) {
    clearCandidateResumeCache();
    if (isGuestOnlyRoute(route)) {
      void router.replace({ path: ROOT_PATH, replace: true });
      return;
    }
    if (needsReloadAfterCrossTabSessionChange(route)) {
      window.location.reload();
    }
    return;
  }

  if (isLoginRequiredRoute(route)) {
    clearCandidateResumeCache();
    void router.replace({ path: ROOT_PATH, query: { redirect: route.fullPath }, replace: true });
    return;
  }
  if (needsReloadAfterCrossTabSessionChange(route)) {
    window.location.reload();
    return;
  }
  if (isDocumentWithoutSpaAppShell()) {
    window.location.reload();
  }
};

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // ブラウザの戻る/進むのときは前のスクロール位置を維持
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash };
    }
    // 求人検索は同一一覧パス内でクエリだけ変えることが多い。ルーター側でトップへ飛ばすと
    // 検索ボタン押下時の即時トップ化と競合するので位置を維持する。
    // 検索実行時は composable 側で snapWindowToTopWithBriefScrollLock を呼ぶ。
    if (
      isJobSearchListRoutePath(to.path) &&
      isJobSearchListRoutePath(from.path) &&
      to.path === from.path
    ) {
      return false;
    }
    return { top: 0 };
  },
});

router.beforeEach(async to => {
  const needsStrictAuthCheck = Boolean(
    to.meta.requiresAuth || to.meta.requiresGuest || to.meta.requiresCandidateResume
  );
  if (!needsStrictAuthCheck) {
    if (appAuthPresentation.value === null) {
      applyInitialAuthPresentationFromDom();
    }
    return true;
  }

  const authenticated = await fetchMeIsAuthenticated({ force: needsStrictAuthCheck });
  syncAppAuthClass(authenticated);

  if (!to.meta.requiresGuest && !to.meta.requiresAuth && !to.meta.requiresCandidateResume) {
    return true;
  }

  if (to.meta.requiresGuest && authenticated) {
    return { path: ROOT_PATH, replace: true };
  }

  if (to.meta.requiresAuth && !authenticated) {
    clearCandidateResumeCache();
    return { path: ROOT_PATH, query: { redirect: to.fullPath }, replace: true };
  }

  if (isCandidateResumeRequiredRoute(to)) {
    const hasResume = await fetchCandidateHasResume();
    if (!hasResume) {
      return { path: EDIT_PROFILE_PATH, replace: true };
    }
  }

  return true;
});

router.afterEach(to => {
  updateDocumentTitle(to);
});

if (typeof window !== 'undefined') {
  const refreshAuthFromServer = (): void => {
    void fetchMeIsAuthenticated({ force: true }).then(auth => {
      const was = appAuthPresentation.value;
      if (was !== auth) {
        syncAppAuthClass(auth);
      }
      reconcileUiAfterCrossTabAuthChange(was, auth);
    });
  };

  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    /**
     * bfcache 復帰時、非 SPA は DOM が凍結したままなので `/api/me` だけでは本文とズレうる。
     * `/setting/*` 等は常にリロードして Rails の `logged_in_using_omniauth?` に合わせる。
     */
    if (isDocumentWithoutSpaAppShell()) {
      window.location.reload();
      return;
    }
    refreshAuthFromServer();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    /** 別タブでログアウトした直後に戻ったとき、TTL キャッシュで古いログイン状態が残らないようにする */
    refreshAuthFromServer();
  });
}

let dynamicImportRecoveryInProgress = false;
router.onError((error, to) => {
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes('Failed to fetch dynamically imported module')) {
    if (dynamicImportRecoveryInProgress) return;
    dynamicImportRecoveryInProgress = true;
    window.location.assign(to?.fullPath || window.location.pathname + window.location.search);
  }
});

export default router;
